import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RefreshCw, CreditCard, CheckCircle2, EyeOff, HandCoins, ExternalLink, Link2 } from "lucide-react";
import { fmtDateTime } from "@/lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const VIEWS = [["needs_review", "Needs Review"], ["assigned", "Assigned"], ["manual_revenue", "Manual Revenue"], ["ignored", "Ignored"], ["all", "All"]];

const Suggestion = ({ s, onAssign, busy }) => (
  <div className="flex flex-wrap items-center justify-between gap-2 bg-black/40 border border-white/10 rounded px-3 py-2">
    <div className="text-xs min-w-0">
      <span className="font-semibold text-white/85">{s.name}</span>
      <span className="text-white/45"> · {s.vehicle} · balance {money(s.balance)}</span>
      <div className="text-white/35 text-[10px]">{(s.reasons || []).join(" · ")}</div>
    </div>
    <div className="flex items-center gap-2">
      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${s.confidence >= 0.85 ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" : s.confidence >= 0.6 ? "text-amber-300 border-amber-400/40 bg-amber-400/10" : "text-white/50 border-white/20"}`}>
        {Math.round(s.confidence * 100)}%
      </span>
      <button onClick={() => onAssign(s.booking_id)} disabled={busy}
        className="text-[10px] font-bold text-[#F2B705] border border-[#F2B705]/40 rounded px-2.5 py-1 hover:bg-[#F2B705] hover:text-black disabled:opacity-50">
        Assign to Booking
      </button>
    </div>
  </div>
);

export default function SquareSyncModule({ token }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [status, setStatus] = useState(null);
  const [view, setView] = useState("needs_review");
  const [items, setItems] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [busy, setBusy] = useState("");
  const [pickFor, setPickFor] = useState("");
  const [pickBooking, setPickBooking] = useState("");

  const load = useCallback(() => {
    axios.get(`${API}/square/status`, auth).then((r) => setStatus(r.data)).catch(() => {});
    axios.get(`${API}/square/payments?view=${view}`, auth).then((r) => setItems(r.data.items))
      .catch(() => toast.error("Could not load Square payments."));
    // eslint-disable-next-line
  }, [token, view]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/bookings`, auth)
      .then((r) => setBookings((r.data || []).filter((b) => b.status !== "cancelled")))
      .catch(() => {});
    // eslint-disable-next-line
  }, [token]);

  const sync = async () => {
    setBusy("sync");
    try {
      const { data } = await axios.post(`${API}/square/sync`, {}, auth);
      toast.success(`Square sync complete — ${data.new} new payment(s), ${data.seen} checked.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed — check the Square token."); }
    finally { setBusy(""); }
  };

  const assign = async (pid, bookingId) => {
    setBusy(pid);
    try {
      const { data } = await axios.post(`${API}/square/payments/${pid}/assign`, { booking_id: bookingId }, auth);
      toast.success(`Payment ${money(data.amount)} applied — balance, ledger, receipt and dashboards updated.`);
      setPickFor(""); setPickBooking(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Assign failed."); }
    finally { setBusy(""); }
  };

  const manual = async (pid) => {
    const note = window.prompt("Mark as manual revenue (not tied to a booking). Optional note:", "");
    if (note === null) return;
    setBusy(pid);
    try { await axios.post(`${API}/square/payments/${pid}/manual-revenue`, { note }, auth); toast.success("Recorded as manual revenue."); load(); }
    catch { toast.error("Failed."); } finally { setBusy(""); }
  };

  const ignore = async (pid) => {
    const note = window.prompt("Ignore/void this payment. Reason (required):", "");
    if (!note) return;
    setBusy(pid);
    try { await axios.post(`${API}/square/payments/${pid}/ignore`, { note }, auth); toast.success("Ignored."); load(); }
    catch { toast.error("Failed."); } finally { setBusy(""); }
  };

  return (
    <div className="space-y-4" data-testid="square-sync-module">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="font-display font-bold text-lg flex items-center gap-2"><CreditCard size={18} className="text-[#F2B705]" /> Square Payment Sync</div>
          {status && (
            <span data-testid="square-connection-chip" className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${status.connected ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" : "text-red-300 border-red-500/40 bg-red-500/10"}`}>
              {status.connected ? `Connected · ${(status.locations || []).map((l) => l.name).join(", ") || "Square"}` : `Not connected — ${status.error || ""}`}
            </span>
          )}
        </div>
        <button data-testid="square-sync-now" onClick={sync} disabled={busy === "sync"}
          className="flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-md bg-[#F2B705] text-black hover:bg-white disabled:opacity-60">
          {busy === "sync" ? <Loader2 className="animate-spin" size={13} /> : <RefreshCw size={13} />} Sync Now (last 7 days)
        </button>
      </div>
      <p className="text-white/45 text-xs -mt-2">In-person Square payments land here automatically (checked every 10 minutes). High-confidence matches are applied to the booking on their own; anything uncertain waits below for your one-click review.</p>

      <div className="flex flex-wrap gap-1.5">
        {VIEWS.map(([k, lbl]) => (
          <button key={k} data-testid={`square-view-${k}`} onClick={() => setView(k)}
            className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${view === k ? "bg-[#F2B705] text-black border-[#F2B705]" : "border-white/15 text-white/60 hover:bg-white/5"}`}>
            {lbl}{status && k === "needs_review" && (status.counts?.needs_review || 0) + (status.counts?.pending || 0) > 0 && ` (${(status.counts.needs_review || 0) + (status.counts.pending || 0)})`}
          </button>
        ))}
      </div>

      {!items ? <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#F2B705]" /></div> : items.length === 0 ? (
        <p className="text-white/40 text-sm border border-white/10 rounded-lg bg-[#0A0A0A] p-6 text-center">
          {view === "needs_review" ? "Nothing needs review — every synced Square payment is matched." : "No payments in this view yet."}
        </p>
      ) : (
        <div className="space-y-2.5">
          {items.map((p) => (
            <div key={p.id} data-testid={`square-payment-${p.id}`} className="bg-[#0C0C0C] border border-white/10 rounded-lg p-4 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <span className="font-display font-bold text-lg text-emerald-400">{money(p.amount)}</span>
                  <span className="ml-3 text-white/80 font-semibold">{p.buyer_name || "Unknown buyer"}</span>
                  <span className="ml-2 text-white/40 text-xs">{p.buyer_phone || ""} {p.buyer_email ? `· ${p.buyer_email}` : ""}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-white/45">
                  <span>{fmtDateTime(p.created_at)}</span>
                  <span>{p.card_brand ? `${p.card_brand} •••${p.card_last4}` : p.source_type || ""}</span>
                  {p.receipt_url && <a href={p.receipt_url} target="_blank" rel="noreferrer" className="text-[#F2B705] flex items-center gap-1 hover:underline"><ExternalLink size={11} /> Receipt</a>}
                </div>
              </div>
              <div className="text-white/30 text-[10px] font-mono">Square ID: {p.id}{p.square_note ? ` · Note: ${p.square_note}` : ""}</div>

              {p.match_status === "assigned" && (
                <div className="text-xs text-emerald-300 flex items-center gap-1.5"><CheckCircle2 size={13} /> Applied to booking {(p.booking_id || "").slice(0, 8)} by {p.assigned_by} {p.assign_note ? `— ${p.assign_note}` : ""}</div>
              )}
              {p.match_status === "manual_revenue" && <div className="text-xs text-sky-300 flex items-center gap-1.5"><HandCoins size={13} /> Manual revenue {p.manual_note ? `— ${p.manual_note}` : ""}</div>}
              {p.match_status === "ignored" && <div className="text-xs text-white/40 flex items-center gap-1.5"><EyeOff size={13} /> Ignored — {p.ignore_reason}</div>}

              {["needs_review", "pending"].includes(p.match_status) && (
                <>
                  {(p.suggestions || []).length > 0 ? (
                    <div className="space-y-1.5">
                      <div className="text-white/45 text-[11px] uppercase tracking-wider">Suggested matches</div>
                      {p.suggestions.map((s) => <Suggestion key={s.booking_id} s={s} busy={busy === p.id} onAssign={(bid) => assign(p.id, bid)} />)}
                    </div>
                  ) : <div className="text-white/40 text-xs">No suggested matches — assign manually below.</div>}
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    {pickFor === p.id ? (
                      <>
                        <select data-testid={`square-pick-booking-${p.id}`} value={pickBooking} onChange={(e) => setPickBooking(e.target.value)}
                          className="bg-black border border-white/15 rounded px-2.5 py-1.5 text-xs text-white min-w-[240px]">
                          <option value="" className="bg-black">Choose a booking…</option>
                          {bookings.map((b) => (
                            <option key={b.id} value={b.id} className="bg-black">
                              {b.name} — {b.vehicle} ({(b.pickup_date || "").slice(0, 10)})
                            </option>
                          ))}
                        </select>
                        <button data-testid={`square-assign-confirm-${p.id}`} onClick={() => pickBooking && assign(p.id, pickBooking)} disabled={!pickBooking || busy === p.id}
                          className="text-[10px] font-bold bg-[#F2B705] text-black rounded px-3 py-1.5 hover:bg-white disabled:opacity-50">Apply Payment</button>
                        <button onClick={() => setPickFor("")} className="text-[10px] text-white/50 hover:text-white">Cancel</button>
                      </>
                    ) : (
                      <button data-testid={`square-assign-other-${p.id}`} onClick={() => { setPickFor(p.id); setPickBooking(""); }}
                        className="text-[10px] font-bold text-white/70 border border-white/20 rounded px-2.5 py-1 hover:bg-white hover:text-black flex items-center gap-1"><Link2 size={10} /> Assign to Booking…</button>
                    )}
                    <button data-testid={`square-manual-${p.id}`} onClick={() => manual(p.id)} disabled={busy === p.id}
                      className="text-[10px] font-bold text-sky-300 border border-sky-500/40 rounded px-2.5 py-1 hover:bg-sky-500 hover:text-black flex items-center gap-1"><HandCoins size={10} /> Manual Revenue</button>
                    <button data-testid={`square-ignore-${p.id}`} onClick={() => ignore(p.id)} disabled={busy === p.id}
                      className="text-[10px] font-bold text-white/50 border border-white/20 rounded px-2.5 py-1 hover:bg-white/10 flex items-center gap-1"><EyeOff size={10} /> Ignore…</button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
